import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close,
  CalendarToday,
  LocationOn,
  Group,
  AttachMoney,
  Home,
  CheckCircle,
  Schedule,
  Cancel,
  Warning,
  Star,
} from '@mui/icons-material';
import { Booking } from '@/types';
import { ReviewPromptModal } from '@/components/Reviews/ReviewPromptModal';
import { ReviewEligibilityService } from '@/utils/review-eligibility-service';

interface TripDetailsModalProps {
  open: boolean;
  onClose: () => void;
  booking: Booking | null;
}

export const TripDetailsModal: React.FC<TripDetailsModalProps> = ({
  open,
  onClose,
  booking,
}) => {
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!booking) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success'
      case 'pending': return 'warning'
      case 'cancelled': return 'error'
      case 'completed': return 'info'
      default: return 'default'
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed': return <CheckCircle />
      case 'pending': return <Schedule />
      case 'cancelled': return <Cancel />
      case 'completed': return <CheckCircle />
      default: return <Schedule />
    }
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    return {
      checkIn: start.toLocaleDateString('en-US', options),
      checkOut: end.toLocaleDateString('en-US', options),
    };
  };

  const calculateNights = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const dates = formatDateRange(booking.startDate, booking.endDate);
  const nights = calculateNights(booking.startDate, booking.endDate);

  // Check if booking is eligible for review
  const isEligibleForReview = ReviewEligibilityService.isBookingCompleted(booking) || 
                              ReviewEligibilityService.hasBookingEnded(booking);

  const handleWriteReview = () => {
    setReviewModalOpen(true);
  };

  const handleReviewModalClose = () => {
    setReviewModalOpen(false);
  };

  const handleReviewSubmitted = () => {
    // Optionally refresh booking data or show success message
    console.log('Review submitted successfully');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" component="h2">
            Trip Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Status and Booking ID */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Booking ID
              </Typography>
              <Typography variant="h6" fontWeight="medium">
                #{booking.bookingId.slice(-8)}
              </Typography>
            </Box>
            <Chip
              label={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              size="medium"
              color={getStatusColor(booking.status) as any}
              icon={getStatusIcon(booking.status)}
            />
          </Box>

          <Divider />

          {/* Stay Information */}
          <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday color="primary" />
              Your Stay
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Check-in
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {dates.checkIn}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Check-out
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {dates.checkOut}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {nights} night{nights !== 1 ? 's' : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Group fontSize="small" />
                  Guests
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {booking.guests} guest{booking.guests !== 1 ? 's' : ''}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Guest Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group color="primary" />
              Guest Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Primary Guest
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {booking.primaryGuestContact?.name || 'Not provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {booking.primaryGuestContact?.email || booking.guestId}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Expected Guests
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {booking.expectedGuests || booking.guests}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Check-in Status
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<CheckCircle fontSize="small" />}
                    label={`${booking.guestCheckInStatus?.completed || 0} completed`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    icon={<Schedule fontSize="small" />}
                    label={`${Math.max(0, (booking.expectedGuests || booking.guests) - (booking.guestCheckInStatus?.completed || 0))} pending`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Payment Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoney color="primary" />
              Payment Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography variant="h5" color="primary" fontWeight="bold">
                  ${booking.totalPrice.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Payment Status
                </Typography>
                <Chip
                  label={booking.paymentStatus.charAt(0).toUpperCase() + booking.paymentStatus.slice(1)}
                  color={booking.paymentStatus === 'paid' ? 'success' : booking.paymentStatus === 'refunded' ? 'error' : 'warning'}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Booking Dates */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Booking Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Booked On
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(booking.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(booking.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Status-specific information */}
          {booking.status === 'cancelled' && (
            <Paper sx={{ p: 3, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                <Warning />
                Booking Cancelled
              </Typography>
              <Typography variant="body2" color="error.dark">
                This booking has been cancelled. If you have any questions, please contact the host.
              </Typography>
            </Paper>
          )}

          {booking.status === 'pending' && (
            <Paper sx={{ p: 3, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
                <Schedule />
                Booking Pending
              </Typography>
              <Typography variant="body2" color="warning.dark">
                Your booking is pending confirmation from the host. You'll receive an update soon.
              </Typography>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        {isEligibleForReview && (
          <Button 
            onClick={handleWriteReview} 
            variant="outlined" 
            startIcon={<Star />}
            sx={{ flex: 1 }}
          >
            Write Review
          </Button>
        )}
        <Button 
          onClick={onClose} 
          variant="contained" 
          sx={{ flex: 1 }}
        >
          Close
        </Button>
      </DialogActions>

      {/* Review Prompt Modal */}
      <ReviewPromptModal
        open={reviewModalOpen}
        onClose={handleReviewModalClose}
        booking={booking}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </Dialog>
  );
};

export default TripDetailsModal;